import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import SearchBox from "../_common/SearchBox";
import ApplicationHistory from "../_common/ApplicationHistory";
import FeedbackPanel from "../_common/FeedbackPanel";
import ScheduleInterviewModal from "./ScheduleInterviewModal";
import InterviewFeedbackModal, { FeedbackData } from "src/components/interview-video/InterviewFeedbackModal";
import AIInterviewReportModal from "src/components/modal/AIInterviewReportModal";
import ProgressIndicator from "../_common/ProgressIndicator";
import SweetAlert from "react-bootstrap-sweetalert";
import { useEmployerJobApi } from "src/_common/hooks/actions/job/appEmployerJobApiHook";
import { useScreeningApi } from "src/_common/hooks/actions/screening/appScreeningApiHook";
import { useInterviewApi } from "src/_common/hooks/actions/interview/appInterviewApiHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useJobListSelector } from "src/_common/hooks/selectors/jobSelector.";
import { CANDIDATE_STATUS, INTERVIEW_STATUS, URLS } from "src/_config";
import { useTranslation } from "src/_common/hooks/useTranslation";
import parse from "html-react-parser";
import moment from "moment";
import { useAppUserDetailsSelector } from "src/_common/hooks/selectors/userSelector";

interface InterviewProps {
  jobId?: string;
  jobData?: any;
  selectedCandidateId?: string | null;
  onMoveToFinalDecision?: (candidateId: string, currentPage?: number) => void;
  currentPage?: number;
  onPageChange?: (page: number) => void;
}

interface InterviewSchedule {
  job_id?: string;
  screening_id?: string;
  member_id?: string;
  status?: number;
  interview_schedule?: string;
  description?: string;
  added_by?: number;
  created_at?: string;
  updated_at?: string;
  id?: string;
  member?: any;
  token_type?: number;
  interview_status?: number;
  interview_url?: string;
  reminder_sent?: number;
  validity_expire_date?: string;
}

interface Candidate {
  name: string;
  appliedFor: string;
  status: any;
  bestMatch: boolean;
  id: string;
  latestScreeningHistory?: {
    status?: number;
    title?: string;
    description?: string;
  };
  latestInterviewSchedule?: InterviewSchedule;
  interview_schedules?: InterviewSchedule[];
  validity_time?: string;
  interview_feedback_details?: Array<{
    screening_id?: string;
    company_user_id?: number;
    user_id?: number;
    attitude?: number;
    communication?: number;
    confidence?: number;
    cultural_fit?: number;
    problem_solving?: number;
    role_knowledge?: number;
    additional_comments?: string;
    overall_score?: number;
    created_at?: string;
    updated_at?: string;
    id?: string;
  }>;
}

interface InterviewStageButton {
  text: string;
  icon: string;
  onClick: () => void;
  variant?: "primary" | "secondary" | "outline" | "cancel";
}

interface InterviewStage {
  id: string;
  title: string;
  description: string;
  icon: string;
  color: string;
  status: "completed" | "current" | "pending";
  buttons?: InterviewStageButton[];
}

export default function Interview({
  jobId,
  jobData,
  selectedCandidateId,
  onMoveToFinalDecision,
  currentPage: propCurrentPage,
  onPageChange,
}: InterviewProps) {
  const { t } = useTranslation("job-details");
  const history = useHistory();
  const [expandedCandidate, setExpandedCandidate] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(propCurrentPage || 1);
  const userDetails = useAppUserDetailsSelector();

  const jobListings = useJobListSelector();
  const currentJob = jobListings?.find((job) => job.emp_job_id == jobId);

  // Sync currentPage with parent component
  useEffect(() => {
    if (propCurrentPage && propCurrentPage !== currentPage) {
      setCurrentPage(propCurrentPage);
    }
  }, [propCurrentPage]);

  // Notify parent when current page changes
  useEffect(() => {
    if (onPageChange && currentPage !== (propCurrentPage || 1)) {
      onPageChange(currentPage);
    }
  }, [currentPage, onPageChange]);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [selectedCandidateForHistory, setSelectedCandidateForHistory] = useState<Candidate | null>(null);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [selectedCandidateForFeedback, setSelectedCandidateForFeedback] = useState<Candidate | null>(null);
  const [showInterviewFeedbackModal, setShowInterviewFeedbackModal] = useState(false);
  const [selectedCandidateForInterviewFeedback, setSelectedCandidateForInterviewFeedback] = useState<Candidate | null>(
    null
  );
  const [isViewModeInterviewFeedback, setIsViewModeInterviewFeedback] = useState(false);
  const [showAIReportModal, setShowAIReportModal] = useState(false);
  const [selectedScheduleId, setSelectedScheduleId] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [candidateToReject, setCandidateToReject] = useState<Candidate | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [interviewPagination, setInterviewPagination] = useState<any>(null);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [candidateToSchedule, setCandidateToSchedule] = useState<Candidate | null>(null);
  const itemsPerPage = 10;

  // API hook
  const employerJobApi = useEmployerJobApi();
  const screeningApi = useScreeningApi();
  const interviewApi = useInterviewApi();
  const toast = useToaster();

  // Fetch interview candidates data from API
  const fetchInterviewCandidates = () => {
    if (!jobId) {
      console.warn("Job ID is not available");
      return;
    }

    setIsLoading(true);

    // Prepare additional parameters for search
    const additionalParams: any = {};
    if (searchTerm && searchTerm.trim()) {
      additionalParams.search = searchTerm.trim();
    }
    additionalParams.status = [
      CANDIDATE_STATUS.SEND_AI_INTERVIEW,
      CANDIDATE_STATUS.SCHEDULE_INTERVIEW,
      CANDIDATE_STATUS.CANDIDATE_ACCEPT_VIA_MAIL,
      CANDIDATE_STATUS.CANDIDATE_REJECT_VIA_MAIL,
      CANDIDATE_STATUS.INTERVIEW_DONE,
    ];

    employerJobApi.callGetScreening(
      jobId,
      itemsPerPage,
      (currentPage - 1) * itemsPerPage,
      additionalParams,
      (message: string, resp: any) => {
        console.log("Interview screening data fetched successfully:", resp);

        // Transform and format the screening data for interview stage
        if (resp && resp.screenings) {
          const formattedCandidates = resp.screenings.map((screening: any) => {
            // Extract candidate name from the new jobseeker_details structure
            let candidateName = "N/A";

            if (screening.jobseeker_details && screening.jobseeker_details.length > 0) {
              const jobseekerDetails = screening.jobseeker_details[0];

              if (jobseekerDetails.basic_details && jobseekerDetails.basic_details.length > 0) {
                const basicDetails = jobseekerDetails.basic_details[0];

                if (basicDetails.name && basicDetails.name.trim()) {
                  candidateName = basicDetails.name.trim();
                } else if (basicDetails.first_name && basicDetails.last_name) {
                  candidateName = `${basicDetails.first_name} ${basicDetails.last_name}`.trim();
                } else if (basicDetails.first_name) {
                  candidateName = basicDetails.first_name.trim();
                }
              }
            }

            return {
              id: screening.id,
              name: candidateName,
              // appliedFor:
              //   screening?.jobseeker_details[0].experience && Array.isArray(screening?.jobseeker_details[0].experience)
              //     ? screening?.jobseeker_details[0].experience
              //         .map((exp: any) => exp.designation)
              //         .filter(Boolean)
              //         .join(", ")
              //     : "",
              appliedFor: currentJob?.job_title,
              status: screening.status,
              bestMatch: (screening.matching_percentage || 0) > 70, // Consider 70%+ as best match
              latestScreeningHistory: screening.latest_screening_history || null,
              latestInterviewSchedule: screening.latest_interview_schedule || null,
              interview_schedules: screening.interview_schedules || [],
              validity_time: screening.validity_time || null,
              interview_feedback_details: screening.interview_feedback_details || [],
            };
          });

          setCandidates(formattedCandidates);
        } else {
          setCandidates([]);
        }

        if (resp && resp.pagination) {
          setInterviewPagination(resp.pagination);
        }

        setIsLoading(false);
      },
      (error: string) => {
        console.error("Error fetching interview candidates:", error);
        toast.error(error);
        setIsLoading(false);
      }
    );
  };

  // useEffect to call API on component mount and when dependencies change
  useEffect(() => {
    if (jobId) {
      fetchInterviewCandidates();
    }
  }, [currentPage, jobId, searchTerm]);

  // useEffect to automatically expand the selected candidate from screening
  useEffect(() => {
    if (selectedCandidateId && candidates?.length > 0) {
      // Find if the selected candidate exists in the current interview candidates
      const candidateExists = candidates.find((c) => c.id === selectedCandidateId);
      if (candidateExists) {
        console.log("Expanding candidate:", selectedCandidateId, candidateExists);
        setExpandedCandidate(selectedCandidateId);
      } else {
        console.log("Candidate not found in current page, might be on different page:", selectedCandidateId);
        // The candidate might be on a different page, but we've already set the correct page from parent
        setExpandedCandidate(selectedCandidateId);
      }
    }
  }, [selectedCandidateId, candidates]);

  const handleSearch = (search: string) => {
    setSearchTerm(search);
    setCurrentPage(1); // Reset to first page when searching
    // The useEffect will trigger fetchInterviewCandidates when searchTerm changes
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1); // Reset to first page when clearing search
  };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    const maxPages = Math.ceil((interviewPagination?.total || 0) / itemsPerPage);
    if (page >= 1 && page <= maxPages) {
      setCurrentPage(page);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    const maxPages = Math.ceil((interviewPagination?.total || 0) / itemsPerPage);
    if (currentPage < maxPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Generate page numbers for display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;
    const maxPages = Math.ceil((interviewPagination?.total || 0) / itemsPerPage);

    if (maxPages <= maxVisiblePages) {
      for (let i = 1; i <= maxPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(maxPages);
      } else if (currentPage >= maxPages - 3) {
        pages.push(1);
        pages.push("...");
        for (let i = maxPages - 4; i <= maxPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(maxPages);
      }
    }

    return pages;
  };

  const getInterviewStages = (candidateId: string): InterviewStage[] => {
    // Find the candidate to check their status
    const candidate = candidates.find((c) => c.id === candidateId);
    if (!candidate) return [];

    const isRejected = candidate?.latestScreeningHistory?.status === 2;
    const isHired = candidate?.latestScreeningHistory?.status === 3;
    const isCompleted = isRejected || isHired;

    // Get AI and Manual interview schedules from interview_schedules array
    const aiInterview = candidate?.interview_schedules?.find((schedule) => schedule.token_type === 2);
    const manualInterview = candidate?.interview_schedules?.find((schedule) => schedule.token_type === 1);

    // AI Interview states
    const hasAIInterview = !!aiInterview;
    const aiInterviewCanceled = aiInterview?.interview_status === 0;
    const aiInterviewScheduled = aiInterview?.interview_status === 1;
    const aiInterviewRescheduled = aiInterview?.interview_status === 2;
    const aiInterviewCompleted = aiInterview?.interview_status === 3;

    // Manual Interview states
    const hasManualInterview = !!manualInterview;
    const manualInterviewCanceled = manualInterview?.interview_status === 0;
    const manualInterviewScheduled = manualInterview?.interview_status === 1;
    const manualInterviewRescheduled = manualInterview?.interview_status === 2;
    const manualInterviewCompleted = manualInterview?.interview_status === 3;
    const manualInterviewApproved = manualInterview?.status === 4;
    const manualInterviewDisapproved = manualInterview?.status === 5;

    // General states
    const candidateAccepted = candidate?.status == CANDIDATE_STATUS.CANDIDATE_ACCEPT_VIA_MAIL;
    const candidateRejected = candidate?.status == CANDIDATE_STATUS.CANDIDATE_REJECT_VIA_MAIL;
    const interviewDone = candidate?.status == CANDIDATE_STATUS.INTERVIEW_DONE;

    return [
      {
        id: "ai-screening",
        title: t("jobDetails.interview.stages.aiScreening.title"),
        description: isCompleted
          ? t("jobDetails.interview.stages.notApplicable")
          : aiInterviewCompleted
          ? t("jobDetails.interview.stages.videoInterview.interviewCompleted")
          : hasAIInterview && interviewDone
          ? t("jobDetails.interview.stages.videoInterview.interviewDone")
          : aiInterviewCanceled
          ? t("jobDetails.interview.stages.aiScreening.initiateDescription")
          : hasAIInterview
          ? t("jobDetails.interview.stages.pendingInterview")
          : t("jobDetails.interview.stages.aiScreening.initiateDescription"),
        icon: "fa-desktop",
        color:
          hasAIInterview && isCompleted
            ? "#A5C5E5"
            : aiInterviewCompleted
            ? "#38A169"
            : hasAIInterview && interviewDone
            ? "#38A169"
            : aiInterviewCanceled
            ? "#A5C5E5"
            : hasAIInterview
            ? "#38A169"
            : "#A5C5E5",
        status: "completed",
        buttons: isCompleted
          ? undefined
          : aiInterviewCompleted
          ? undefined
          : hasAIInterview && !aiInterviewCanceled
          ? [
              {
                text: t("jobDetails.interview.stages.aiScreening.resendButton"),
                icon: "fa-paper-plane",
                onClick: () => handleSendAIInterview(candidate, INTERVIEW_STATUS.RESCHEDULE),
              } as InterviewStageButton,
              {
                text: t("jobDetails.interview.stages.aiScreening.cancelButton"),
                icon: "fa-ban",
                onClick: () => handleRescheduleCancelInterview(aiInterview, INTERVIEW_STATUS.CANCEL),
                variant: "cancel" as const,
              } as InterviewStageButton,
            ]
          : [
              {
                text: t("jobDetails.interview.stages.aiScreening.sendButton"),
                icon: "fa-paper-plane",
                onClick: () => handleSendAIInterview(candidate),
              } as InterviewStageButton,
            ],
      },
      {
        id: "video-interview",
        title: t("jobDetails.interview.stages.videoInterview.title"),
        description: isCompleted
          ? t("jobDetails.interview.stages.notApplicable")
          : manualInterviewCompleted
          ? t("jobDetails.interview.stages.videoInterview.interviewCompleted")
          : hasManualInterview && interviewDone
          ? t("jobDetails.interview.stages.videoInterview.interviewDone")
          : manualInterviewCanceled
          ? t("jobDetails.interview.stages.videoInterview.scheduleManual")
          : hasManualInterview
          ? (() => {
              let lines = [];

              if (!manualInterviewDisapproved) {
                lines.push(
                  t("jobDetails.interview.stages.videoInterview.scheduledDescription") +
                    (manualInterview?.member?.name
                      ? " " + t("jobDetails.interview.stages.videoInterview.with") + " " + manualInterview?.member?.name
                      : "") +
                    (manualInterview?.interview_schedule
                      ? " - " + moment(manualInterview?.interview_schedule).format("MMM DD, YYYY HH:mm")
                      : "") +
                    (!manualInterviewApproved
                      ? " ( " + t("jobDetails.interview.stages.videoInterview.pending") + " )"
                      : manualInterviewApproved
                      ? " ( " + t("jobDetails.interview.stages.videoInterview.memberAccepted") + " )"
                      : "")
                );

                if (manualInterviewApproved) {
                  lines.push(t("jobDetails.interview.stages.pendingInterview"));
                } else if (candidateAccepted) {
                  lines.push(t("jobDetails.interview.stages.videoInterview.candidateAccepted"));
                } else if (candidateRejected) {
                  lines.push(t("jobDetails.interview.stages.videoInterview.candidateRejected"));
                }
              }

              return lines.join(" <br/> ");
            })()
          : t("jobDetails.interview.stages.videoInterview.scheduleManual"),
        icon: "fa-video-camera",
        color:
          hasManualInterview && isCompleted
            ? "#A5C5E5"
            : manualInterviewCompleted
            ? "#38A169"
            : hasManualInterview && interviewDone
            ? "#38A169"
            : manualInterviewCanceled
            ? "#A5C5E5"
            : hasManualInterview
            ? "#38A169"
            : "#A5C5E5",
        status: interviewDone || manualInterviewCompleted ? "completed" : "current",
        buttons: isCompleted
          ? undefined
          : manualInterviewCompleted
          ? undefined
          : hasManualInterview && !manualInterviewCanceled
          ? manualInterview?.reminder_sent == 1 && manualInterview?.interview_url
            ? [
                {
                  text: t("jobDetails.interview.stages.videoInterview.startButton"),
                  icon: "fa-play",
                  onClick: () => window.open(manualInterview?.interview_url, "_blank"),
                } as InterviewStageButton,
              ]
            : [
                {
                  text: t("jobDetails.interview.stages.videoInterview.rescheduleButton"),
                  icon: "fa-calendar",
                  onClick: () => handleScheduleInterview(candidate),
                } as InterviewStageButton,
                {
                  text: t("jobDetails.interview.stages.videoInterview.cancelButton"),
                  icon: "fa-ban",
                  onClick: () => handleRescheduleCancelInterview(manualInterview, INTERVIEW_STATUS.CANCEL),
                  variant: "cancel" as const,
                } as InterviewStageButton,
              ]
          : [
              {
                text: t("jobDetails.interview.stages.videoInterview.scheduleButton"),
                icon: "fa-calendar",
                onClick: () => handleScheduleInterview(candidate),
              } as InterviewStageButton,
            ],
      },
      {
        id: "final-decision",
        title: t("jobDetails.interview.stages.finalDecision.title"),
        description: isRejected
          ? t("jobDetails.interview.stages.finalDecision.candidateRejected")
          : isHired
          ? t("jobDetails.interview.stages.finalDecision.candidateHired")
          : t("jobDetails.interview.stages.finalDecision.pendingDescription"),
        icon: "fa-flag-checkered",
        color: isRejected ? "#ef4444" : isHired ? "#38A169" : "#E5E7EB",
        status: isCompleted ? "completed" : "pending",
      },
    ];
  };

  const handleCandidateToggle = (candidateId: string) => {
    setExpandedCandidate(expandedCandidate === candidateId ? null : candidateId);
  };

  const handleSendAIInterview = (candidate: Candidate, status?: number) => {
    if (!jobId || !candidate?.id) return;

    // If status is provided (reschedule), directly call handleRescheduleCancelInterview
    if (status) {
      handleRescheduleCancelInterview(candidate, status);
      return;
    }

    const payload: any = {
      job_id: jobId,
      screening_id: candidate.id,
    };
    interviewApi.callScheduleRequestAI(
      payload,
      (message: string, resp: any) => {
        toast.success(t("jobDetails.interview.aiInterviewSent"));
        screeningApi.callUpdateStatus(
          {
            screening_id: candidate?.id,
            status: CANDIDATE_STATUS.SEND_AI_INTERVIEW,
          },
          (message: string, resp: any) => {
            console.log("Candidate status updated to AI Interview:", resp);
            handleCloseScheduleModal();
            // Optionally refresh screening data
            fetchInterviewCandidates();
          },
          (error: string) => {
            toast.error(error);
          }
        );
      },
      (error: string) => {
        toast.error(error);
      }
    );
  };

  const handleRescheduleCancelInterview = (interview: any, status: number) => {
    if (!interview?.id) {
      toast.error("No interview schedule found");
      return;
    }

    interviewApi.callRescheduleOrCancel(
      {
        schedule_id: interview?.id,
        interview_status: status,
      },
      (message: string, resp: any) => {
        // Optionally refresh screening data
        fetchInterviewCandidates();
      },
      (error: string) => {}
    );
  };

  const handleScheduleInterview = (candidate: Candidate) => {
    setCandidateToSchedule(candidate);
    setIsScheduleModalOpen(true);
  };

  const handleCloseScheduleModal = () => {
    setIsScheduleModalOpen(false);
    setCandidateToSchedule(null);
  };

  const handleScheduleConfirm = (scheduleData: any) => {
    console.log("Interview scheduled with data:", scheduleData);
    console.log("For candidate:", candidateToSchedule);

    screeningApi.callUpdateStatus(
      {
        screening_id: candidateToSchedule?.id,
        status: CANDIDATE_STATUS.SCHEDULE_INTERVIEW,
      },
      (message: string, resp: any) => {
        console.log("Candidate status updated to Scheduled Interview:", resp);
        handleCloseScheduleModal();
        // Optionally refresh screening data
        fetchInterviewCandidates();
      },
      (error: string) => {
        toast.error("Failed to update candidate status: " + error);
      }
    );
  };

  const handleViewAIReport = (candidate: Candidate) => {
    console.log("View AI Report for candidate:", candidate);

    // Get the AI interview schedule from the interview_schedules array
    const aiInterview = candidate?.interview_schedules?.find((schedule) => schedule.token_type === 2);
    const scheduleId = aiInterview?.id;

    if (!scheduleId) {
      toast.error("No AI interview schedule found for this candidate");
      return;
    }

    // Open the AI report modal
    setSelectedScheduleId(scheduleId);
    setShowAIReportModal(true);
  };

  const handleCloseAIReportModal = () => {
    setShowAIReportModal(false);
    setSelectedScheduleId("");
  };

  const handleAddFeedback = () => {
    console.log("Add Feedback");
  };

  const handleAddFeedbackForCandidate = (candidate: Candidate) => {
    setSelectedCandidateForFeedback(candidate);
    setIsFeedbackOpen(true);
  };

  const handleCloseFeedback = () => {
    setIsFeedbackOpen(false);
    setSelectedCandidateForFeedback(null);
  };

  const handleViewHistory = () => {
    console.log("View History");
  };

  const handleViewHistoryForCandidate = (candidate: Candidate) => {
    if (candidate && candidate.id) {
      // Open the ApplicationHistory panel - it will handle the API call internally
      setSelectedCandidateForHistory(candidate);
      setIsHistoryOpen(true);
    } else {
      console.warn("No screening ID found for candidate:", candidate.id);
      toast.error(t("jobDetails.interview.noScreeningId"));
    }
  };

  const handleCloseHistory = () => {
    setIsHistoryOpen(false);
    setSelectedCandidateForHistory(null);
  };

  const handleMoveToFinalDecision = (candidate: Candidate) => {
    console.log("Move to Final Decision for candidate:", candidate);
    const feedbackPayload = {
      // ...(userDetails?.user_type != "member" ? { member_id: userDetails?.user?.id } : {}),
      screening_id: candidate.id,
      application_type: 1,
      status: 9,
      title: t("jobDetails.screening.apiPayload.movedToFinalDecisionTitle"),
      description: t("jobDetails.screening.apiPayload.movedToFinalDecisionDescription"),
    };

    screeningApi.callAddFeedback(
      feedbackPayload,
      (message: string, resp: any) => {
        console.log("Interview schedule feedback recorded successfully:", resp);

        screeningApi.callUpdateStatus(
          {
            screening_id: candidate.id,
            status: CANDIDATE_STATUS.FINAL_DECISION,
          },
          (message: string, resp: any) => {
            if (onMoveToFinalDecision && candidate.id) {
              onMoveToFinalDecision(candidate.id, currentPage);
            }
          },
          (error: string) => {
            toast.error("Failed to update candidate status: " + error);
          }
        );
      },
      (error: string) => {}
    );
  };

  const handleRejectCandidate = (candidate: Candidate) => {
    setCandidateToReject(candidate);
    setShowRejectConfirm(true);
  };

  const handleConfirmReject = () => {
    if (candidateToReject) {
      // Call addhistory API with rejection parameters
      const payload = {
        ...(userDetails?.user_type != "member" ? { member_id: userDetails?.user?.id } : {}),
        screening_id: candidateToReject.id,
        application_type: 1,
        status: 2,
        title: t("jobDetails.interview.apiPayload.rejectTitle"),
        description: t("jobDetails.interview.apiPayload.rejectDescription"),
      };

      screeningApi.callAddFeedback(
        payload,
        (message: string, resp: any) => {
          console.log("Candidate rejection recorded successfully:", resp);
          toast.success(t("jobDetails.interview.rejectSuccess"));

          // Refresh the screening data to get updated candidate status
          fetchInterviewCandidates();
        },
        (error: string) => {
          console.error("Error recording candidate rejection:", error);
          toast.error(t("jobDetails.interview.rejectError") + ": " + error);
        }
      );

      // Also update candidate status to REJECTED
      screeningApi.callUpdateStatus(
        {
          screening_id: candidateToReject?.id,
          status: CANDIDATE_STATUS.REJECTED,
        },
        (message: string, resp: any) => {
          // status updated
        },
        (error: string) => {
          // silent fail to not block UX
        }
      );

      // Close the modal
      setShowRejectConfirm(false);
      setCandidateToReject(null);
    }
  };

  const handleCancelReject = () => {
    setShowRejectConfirm(false);
    setCandidateToReject(null);
  };

  const handleOpenInterviewFeedback = (candidate: Candidate) => {
    setSelectedCandidateForInterviewFeedback(candidate);
    setIsViewModeInterviewFeedback(false);
    setShowInterviewFeedbackModal(true);
  };

  const handleViewInterviewFeedback = (candidate: Candidate) => {
    setSelectedCandidateForInterviewFeedback(candidate);
    setIsViewModeInterviewFeedback(true);
    setShowInterviewFeedbackModal(true);
  };

  const handleInterviewFeedbackSubmit = (feedbackData: FeedbackData) => {
    if (!selectedCandidateForInterviewFeedback?.id) {
      toast.error("Unable to submit feedback: Missing candidate information");
      setShowInterviewFeedbackModal(false);
      return;
    }

    // Close the modal and refresh the candidate list
    setShowInterviewFeedbackModal(false);
    setSelectedCandidateForInterviewFeedback(null);
    fetchInterviewCandidates(); // Refresh the list
  };

  const handleInterviewFeedbackCancel = () => {
    setShowInterviewFeedbackModal(false);
    setSelectedCandidateForInterviewFeedback(null);
    setIsViewModeInterviewFeedback(false);
  };

  const calculateOverallScore = (feedbackData: FeedbackData): string => {
    const numericRatings = [
      feedbackData.attitude,
      feedbackData.communication,
      feedbackData.confidence,
      feedbackData.culturalFit,
      feedbackData.problemSolving,
      feedbackData.roleKnowledge,
    ];
    const sum = numericRatings.reduce((acc, curr) => acc + curr, 0);
    const average = sum / 6;
    return average.toFixed(1);
  };

  return (
    <div className="interview-wrapper">
      {/* Header */}
      <div className="interview-header">
        <div className="header-left-ai-job">
          <div className="job-overview-section">
            <h1 className="job-overview-title">{currentJob?.job_title}</h1>
            <p className="job-overview-subtitle">{t("jobDetails.interview.subtitle")}</p>
          </div>
        </div>
        {/* Only show search when there are candidates or search is active or loading */}
        {(candidates?.length > 0 || searchTerm || isLoading) && (
          <div className="header-search">
            <SearchBox
              placeholder={t("jobDetails.interview.searchPlaceholder")}
              onSearch={handleSearch}
              onClear={handleClearSearch}
            />
          </div>
        )}
      </div>

      {/* Candidates List */}
      <div className="interview-candidates-container">
        {isLoading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
          </div>
        ) : candidates.length > 0 ? (
          candidates.map((candidate) => (
            <div key={candidate.id} className="interview-candidate-card">
              {/* Candidate Header */}
              <div className="candidate-header" onClick={() => handleCandidateToggle(candidate.id)}>
                <div className="candidate-info">
                  <h3 className="candidate-name">
                    {candidate.name === "N/A" ? t("jobDetails.interview.notAvailable") : candidate.name}
                  </h3>
                  <p className="candidate-position">
                    {candidate.appliedFor ? t("jobDetails.interview.appliedFor") + " " + candidate.appliedFor : ""}
                    {candidate?.validity_time && (
                      <span className="status-badge hired">
                        {t("jobDetails.interview.validTill")} {candidate.validity_time}{" "}
                        {t("jobDetails.interview.hours")}
                      </span>
                    )}
                  </p>
                </div>
                <div className="candidate-right-section">
                  {candidate.bestMatch && (
                    <div className="best-match-container">
                      <span className="best-match-badge">
                        <i className="fa fa-circle"></i>
                        {t("jobDetails.interview.bestMatch")}
                      </span>
                      <span className="best-match-description">{t("jobDetails.interview.bestMatchDescription")}</span>
                    </div>
                  )}
                  <button className={`expand-btn ${expandedCandidate == candidate.id ? "expanded" : ""}`}>
                    <i className="fa fa-chevron-right"></i>
                  </button>
                </div>
              </div>

              {/* Expanded Content */}
              {expandedCandidate === candidate.id && (
                <div className="candidate-expanded-content">
                  <div className="interview-process-section">
                    {/* Interview Process */}
                    <div className="interview-process">
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "20px",
                        }}
                      >
                        <h3>{t("jobDetails.interview.processTitle")}</h3>
                        {(!candidate.interview_feedback_details ||
                          candidate.interview_feedback_details.length === 0) && (
                          <button
                            className="stage-button secondary"
                            onClick={() => handleOpenInterviewFeedback(candidate)}
                            style={{ margin: 0 }}
                          >
                            <i className="fa fa-star"></i>
                            {t("jobDetails.interview.stages.videoInterview.feedbackButton")}
                          </button>
                        )}
                      </div>

                      <div className="interview-stages">
                        {getInterviewStages(candidate.id).map((stage) => (
                          <div key={stage.id} className={`interview-stage-item ${stage.status}`}>
                            <div className="stage-icon" style={{ backgroundColor: stage.color }}>
                              <i className={`fa ${stage.icon}`}></i>
                            </div>
                            <div className="stage-content">
                              <h4>{stage.title}</h4>
                              <p>{parse(stage?.description)}</p>
                              {stage.buttons && stage.buttons.length > 0 && stage.status !== "pending" && (
                                <div className="stage-buttons-group">
                                  {stage.buttons.map((button, index) => (
                                    <button
                                      key={index}
                                      className={`stage-button ${
                                        stage.status === "completed" ? "completed" : "current"
                                      } ${button.variant || "primary"}`}
                                      onClick={button.onClick}
                                    >
                                      <i className={`fa ${button.icon}`}></i>
                                      {button.text}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Actions Panel */}
                    <div className="actions-panel">
                      <div className="actions-wrapper">
                        <div className="actions-header">
                          <h3>{t("jobDetails.interview.actionsTitle")}</h3>
                        </div>

                        <div className="action-buttons-list">
                          {(() => {
                            // Get AI interview from interview_schedules
                            const aiInterview = candidate?.interview_schedules?.find(
                              (schedule) => schedule.token_type === 2
                            );
                            const aiInterviewCompleted = aiInterview?.interview_status === INTERVIEW_STATUS.COMPLETE;
                            console.log(aiInterview, aiInterviewCompleted);

                            return (
                              aiInterviewCompleted && (
                                <button className="action-list-btn" onClick={() => handleViewAIReport(candidate)}>
                                  <i className="fa fa-file-text"></i>
                                  {t("jobDetails.interview.viewAIReport")}
                                </button>
                              )
                            );
                          })()}
                          <button className="action-list-btn" onClick={() => handleAddFeedbackForCandidate(candidate)}>
                            <i className="fa fa-edit"></i>
                            {t("jobDetails.interview.addFeedback")}
                          </button>
                          {candidate.interview_feedback_details && candidate.interview_feedback_details.length > 0 && (
                            <button className="action-list-btn" onClick={() => handleViewInterviewFeedback(candidate)}>
                              <i className="fa fa-eye"></i>
                              View Live Interview Report
                            </button>
                          )}
                          <button className="action-list-btn" onClick={() => handleViewHistoryForCandidate(candidate)}>
                            <i className="fa fa-clock"></i>
                            {t("jobDetails.interview.viewHistory")}
                          </button>
                        </div>
                      </div>

                      <div className="decision-section">
                        {candidate.latestScreeningHistory?.status == 2 ? (
                          <div className="decision-buttons">
                            <button className="btn-reject" disabled>
                              <i className="fa fa-times"></i>
                              {t("jobDetails.interview.candidateRejected")}
                            </button>
                          </div>
                        ) : candidate.latestScreeningHistory?.status == 3 ? (
                          <div className="decision-buttons">
                            <button className="btn-move-to-final" disabled>
                              <i className="fa fa-times"></i>
                              {t("jobDetails.interview.candidateHired")}
                            </button>
                          </div>
                        ) : (
                          <>
                            <h4>{t("jobDetails.interview.makeDecision")}</h4>
                            <div className="decision-buttons">
                              <button
                                className="btn-move-to-final"
                                onClick={() => handleMoveToFinalDecision(candidate)}
                              >
                                <i className="fa fa-check"></i>
                                {t("jobDetails.interview.moveToFinal")}
                              </button>
                              <button className="btn-reject" onClick={() => handleRejectCandidate(candidate)}>
                                <i className="fa fa-times"></i>
                                {t("jobDetails.interview.rejectCandidate")}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        ) : !isLoading && candidates.length === 0 ? (
          <ProgressIndicator stage="interview" />
        ) : (
          <div className="loading-container" style={{ padding: "60px 20px" }}>
            <span style={{ color: "#666", fontSize: "16px" }}>{t("jobDetails.interview.noCandidates")}</span>
          </div>
        )}
      </div>

      {/* Pagination */}
      {candidates.length > 0 && Math.ceil((interviewPagination?.total || 0) / itemsPerPage) > 1 ? (
        <div className="pagination">
          <button className="page-btn" onClick={handlePrevPage} disabled={currentPage === 1}>
            {t("jobDetails.interview.prev")}
          </button>
          <div className="page-numbers">
            {getPageNumbers().map((page, index) =>
              page === "..." ? (
                <span key={`ellipsis-${index}`}>...</span>
              ) : (
                <button
                  key={page}
                  className={`page-num ${currentPage === page ? "active" : ""}`}
                  onClick={() => handlePageChange(page as number)}
                >
                  {page}
                </button>
              )
            )}
          </div>
          <button
            className="page-btn"
            onClick={handleNextPage}
            disabled={currentPage === Math.ceil((interviewPagination?.total || 0) / itemsPerPage)}
          >
            {t("jobDetails.interview.next")}
          </button>
        </div>
      ) : null}

      {/* Application History Panel */}
      <ApplicationHistory
        candidateName={selectedCandidateForHistory?.name || ""}
        screeningId={selectedCandidateForHistory?.id}
        isOpen={isHistoryOpen}
        onClose={handleCloseHistory}
      />

      {/* Feedback Panel */}
      <FeedbackPanel
        candidateName={selectedCandidateForFeedback?.name || ""}
        screeningId={selectedCandidateForFeedback?.id || ""}
        isOpen={isFeedbackOpen}
        onClose={handleCloseFeedback}
      />

      {/* Reject Confirmation Modal */}
      {showRejectConfirm && candidateToReject && (
        <SweetAlert
          warning
          showCancel
          confirmBtnText={t("jobDetails.interview.confirmReject.yesReject")}
          confirmBtnBsStyle="danger"
          cancelBtnText={t("jobDetails.interview.confirmReject.cancel")}
          cancelBtnBsStyle="default"
          title={t("jobDetails.interview.confirmReject.title")}
          onConfirm={handleConfirmReject}
          onCancel={handleCancelReject}
        >
          {t("jobDetails.interview.confirmReject.message")} <strong>{candidateToReject.name}</strong>?
          <br />
          <br />
          {t("jobDetails.interview.confirmReject.warning")}
        </SweetAlert>
      )}

      {/* Schedule Interview Modal */}
      <ScheduleInterviewModal
        isOpen={isScheduleModalOpen}
        onClose={handleCloseScheduleModal}
        candidateName={candidateToSchedule?.name || ""}
        candidateId={candidateToSchedule?.id || ""}
        jobId={jobId || ""}
        onSchedule={handleScheduleConfirm}
      />

      {/* Interview Feedback Modal */}
      {showInterviewFeedbackModal && selectedCandidateForInterviewFeedback && (
        <InterviewFeedbackModal
          isVisible={showInterviewFeedbackModal}
          onSubmit={handleInterviewFeedbackSubmit}
          onCancel={handleInterviewFeedbackCancel}
          screeningId={selectedCandidateForInterviewFeedback.id}
          isViewMode={isViewModeInterviewFeedback}
          existingFeedback={
            isViewModeInterviewFeedback &&
            selectedCandidateForInterviewFeedback.interview_feedback_details &&
            selectedCandidateForInterviewFeedback.interview_feedback_details.length > 0
              ? {
                  attitude: selectedCandidateForInterviewFeedback.interview_feedback_details[0].attitude || 0,
                  communication: selectedCandidateForInterviewFeedback.interview_feedback_details[0].communication || 0,
                  confidence: selectedCandidateForInterviewFeedback.interview_feedback_details[0].confidence || 0,
                  culturalFit: selectedCandidateForInterviewFeedback.interview_feedback_details[0].cultural_fit || 0,
                  problemSolving:
                    selectedCandidateForInterviewFeedback.interview_feedback_details[0].problem_solving || 0,
                  roleKnowledge:
                    selectedCandidateForInterviewFeedback.interview_feedback_details[0].role_knowledge || 0,
                  additionalComments:
                    selectedCandidateForInterviewFeedback.interview_feedback_details[0].additional_comments || "",
                }
              : undefined
          }
        />
      )}

      {/* AI Interview Report Modal */}
      {showAIReportModal && selectedScheduleId && (
        <AIInterviewReportModal
          show={showAIReportModal}
          onHide={handleCloseAIReportModal}
          scheduleId={selectedScheduleId}
        />
      )}
    </div>
  );
}
